import { useEffect, useMemo, useState, useCallback } from "react";
import {
    Grid,
    Alert,
    Button,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
    BasePage,
    Loader,
    TextInput,
    GenderInput,
    BirthdayInput,
    LoadingButton,
    LinkButton,
} from "components";
import { useAuthentication, useSnack } from "hooks";
import { userService, authenticationService, uploadProfileImage } from "services";
import { AvatarPicker } from "components";
import { parseBirthdayToDayjs, formatBirthday } from "utilities";

export function ProfilePage() {
    const { user, loading } = useAuthentication();
    const uid = useMemo(() => user?.uid || null, [user]);

    const [isLoading, setIsLoading] = useState(false);
    const [fetchError, setFetchError] = useState("");
    const [profile, setProfile] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState("");
    const [retryCount, setRetryCount] = useState(0);

    const MAX_RETRIES = 5;

    const [form, setForm] = useState({
        firstName: "",
        lastName: "",
        birthday: null,
        gender: "",
    });

    const { openSnack } = useSnack();

    const [isBirthdayValid, setIsBirthdayValid] = useState(true);
    const [avatarFile, setAvatarFile] = useState(null);
    const [avatarPreview, setAvatarPreview] = useState(null);
    const [avatarUploading, setAvatarUploading] = useState(false);

    useEffect(() => {
        let cancelled = false;
        let retryTimer = null;
        
        async function loadProfile() {
            if (!uid) return;
            
            setIsLoading(true);
            setFetchError("");
            
            try {
                const doc = await userService.getUserDoc(uid);
                
                if (!cancelled) {
                    if (doc) {
                        setProfile(doc);
                        setAvatarPreview(doc?.photoUrl || null);
                        setForm({
                            firstName: doc?.firstName ?? "",
                            lastName: doc?.lastName ?? "",
                            birthday: parseBirthdayToDayjs(doc?.birthday),
                            gender: doc?.gender ?? "",
                        });
                        setRetryCount(0);
                        setIsLoading(false);
                    } else if (retryCount < MAX_RETRIES) {
                        console.warn('Profile not found, retrying in ' + Math.min(500 * Math.pow(2, retryCount), 5000) + 'ms...');
                        const delay = Math.min(500 * Math.pow(2, retryCount), 5000);
                        retryTimer = setTimeout(() => {
                            if (!cancelled) setRetryCount(prev => prev + 1);
                        }, delay);
                    } else {
                        console.warn('Profile not found after ' + MAX_RETRIES + ' retries');
                        setProfile(null);
                        setIsLoading(false);
                    }
                }
            } catch (err) {
                console.error('Error loading profile:', err);
                if (!cancelled) {
                    if (retryCount < MAX_RETRIES) {
                        const delay = Math.min(500 * Math.pow(2, retryCount), 5000);
                        retryTimer = setTimeout(() => {
                            if (!cancelled) setRetryCount(prev => prev + 1);
                        }, delay);
                    } else {
                        setFetchError("Could not load your profile.");
                        setIsLoading(false);
                    }
                }
            }
        }
        
        loadProfile();
        
        return () => { 
            cancelled = true;
            if (retryTimer) clearTimeout(retryTimer);
        };
    }, [uid, retryCount]);

    const setFormField = useCallback(
        (field) => (value) => {
            setForm((f) => (f[field] === value ? f : { ...f, [field]: value }));
        },
        []
    );

    const handleBirthdayValidation = useCallback((ok) => {
        setIsBirthdayValid((prev) => (prev === ok ? prev : ok));
    }, []);

    const handleRemoveAvatar = () => {
        setAvatarFile(null);
        setAvatarPreview(null);
    };

    const isDirty = useMemo(() => {
        if (!profile) return false;

        const photoChanged = avatarPreview !== (profile?.photoUrl || null);

        const before = {
            firstName: profile?.firstName ?? "",
            lastName: profile?.lastName ?? "",
            birthday: profile?.birthday || null,
            gender: profile?.gender ?? "",
        };
        const after = {
            firstName: form.firstName,
            lastName: form.lastName,
            birthday: formatBirthday(form.birthday) || null,
            gender: form.gender,
        };
        return photoChanged || JSON.stringify(before) !== JSON.stringify(after);
    }, [profile, form, avatarPreview]);

    const handleSave = async () => {
        if (!uid) return;
        setSaveError("");
        setSaving(true);
        try {
            let photoUrl = undefined;

            if (avatarFile) {
                setAvatarUploading(true);
                try {
                    photoUrl = await uploadProfileImage(uid, avatarFile);
                    setAvatarFile(null);
                } finally {
                    setAvatarUploading(false);
                }
            } else if (avatarPreview === null && profile.photoUrl) {
                // Case: Explicitly deleted an existing photo
                photoUrl = null; 
            }

            const body = {
                firstName: form.firstName.trim(),
                lastName: form.lastName.trim(),
                birthday: formatBirthday(form.birthday) || null,
                gender: form.gender || "",
                ...(photoUrl !== undefined ? { photoUrl } : {}),
            };

            const res = await authenticationService.makeAuthenticatedRequest(
                `${process.env.REACT_APP_API_URL}/user/profile`,
                { method: "POST", body: JSON.stringify(body) },
            );

            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.error || "Failed to save profile");
            }

            // Update local profile state
            const updatedProfile = {
                ...profile,
                ...body,
                ...(photoUrl !== undefined ? { photoUrl } : {}),
            };

            setProfile(updatedProfile);
            if (photoUrl !== undefined) setAvatarPreview(photoUrl);
            setEditMode(false);
            openSnack("Profile updated!", "success");
        } catch (err) {
            console.error(err);
            setSaveError(err.message || "Failed to save your profile.");
            openSnack(err.message || "Failed to save your profile.", "error");
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        if (!profile) return;
        setForm({
            firstName: profile?.firstName ?? "",
            lastName: profile?.lastName ?? "",
            birthday: parseBirthdayToDayjs(profile?.birthday),
            gender: profile?.gender ?? "",
        });
        // Reset avatar states
        setAvatarPreview(profile?.photoUrl || null);
        setAvatarFile(null);
        setSaveError("");
        setEditMode(false);
    };

    if (loading || isLoading) {
        const message = retryCount > 0 ? "Setting up your profile..." : "Loading profile...";
        return <Loader loadingMessage={message} />;
    }

    if (!loading && !uid) {
        return (
            <BasePage title="Profile">
                <Alert severity="warning">Please sign in to view your profile.</Alert>
            </BasePage>
        );
    }

    return (
        <BasePage title="Profile">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Grid container spacing={2}>
                    {fetchError && (
                        <Grid size={12}>
                            <Alert severity="error">
                                {fetchError}
                                <Button size="small" onClick={() => { setRetryCount(0); setFetchError(""); }} sx={{ ml: 2 }}>
                                    Retry
                                </Button>
                            </Alert>
                        </Grid>
                    )}

                    {profile ? (
                        <>
                            <Grid size={12} sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                                <AvatarPicker
                                    src={avatarPreview}
                                    initials={`${form.firstName?.charAt(0) || ''}${form.lastName?.charAt(0) || ''}`.toUpperCase() || 'U'}
                                    size={80}
                                    onChange={(file) => {
                                        setAvatarFile(file);
                                        setAvatarPreview(URL.createObjectURL(file));
                                    }}
                                    onRemove={handleRemoveAvatar}
                                    disabled={!editMode || saving || avatarUploading}
                                />
                            </Grid>
                            
                            <Grid size={12}>
                                <TextInput
                                    label="First name"
                                    value={form.firstName}
                                    onChange={setFormField("firstName")}
                                    required
                                    disabled={!editMode}
                                    autoComplete="given-name"
                                />
                            </Grid>

                            <Grid size={12}>
                                <TextInput
                                    label="Last name"
                                    value={form.lastName}
                                    onChange={setFormField("lastName")}
                                    required
                                    disabled={!editMode}
                                    autoComplete="family-name"
                                />
                            </Grid>

                            <Grid size={12}>
                                <BirthdayInput
                                    label="Birthday"
                                    value={form.birthday}
                                    onChange={setFormField("birthday")}
                                    onValidationChange={handleBirthdayValidation}
                                    required
                                    minAge={18}
                                    disabled={!editMode}
                                />
                            </Grid>

                            <Grid size={12}>
                                <GenderInput
                                    value={form.gender}
                                    onChange={setFormField("gender")}
                                    disabled={!editMode}
                                />
                            </Grid>

                            {saveError && (
                                <Grid size={12}>
                                    <Alert severity="error">{saveError}</Alert>
                                </Grid>
                            )}

                            <Grid size={12} rowGap={1}>
                                {!editMode ? (
                                    <Button fullWidth variant="contained" onClick={() => setEditMode(true)}>
                                        Edit
                                    </Button>
                                ) : (
                                    <LoadingButton
                                        variant="contained"
                                        onClick={handleSave}
                                        loading={saving}
                                        disabled={saving || !isDirty || !isBirthdayValid}
                                    >
                                        Save
                                    </LoadingButton>
                                )}
                            </Grid>

                            <Grid size={12} className="form-actions">
                                {editMode ? (
                                    <Button variant="text" onClick={handleCancel} disabled={saving}>
                                        Cancel
                                    </Button>
                                ) : (
                                    <LinkButton to="/settings">Settings</LinkButton>
                                )}
                            </Grid>
                        </>
                    ) : (
                        <Grid size={12}>
                            <Alert severity="info">
                                No profile found.
                                <Button size="small" onClick={() => window.location.reload()} sx={{ ml: 2 }}>
                                    Refresh Page
                                </Button>
                            </Alert>
                        </Grid>
                    )}
                </Grid>
            </LocalizationProvider>
        </BasePage>
    );
}